import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { fmtDateTime } from "@/lib/format";

export const NotificationBell = ({ light = false }) => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <Popover onOpenChange={(o) => { if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className={`press relative p-2 ${light ? "text-white/80 hover:text-white" : "text-neutral-600 hover:text-black"}`}>
          <Bell className="w-5 h-5" strokeWidth={1.5} />
          {data.unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center bg-[#C5A059] text-[#0A0A0C] text-[10px] font-bold rounded-full">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl overflow-hidden" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="eyebrow">{t("c.notifications")}</span>
          {data.unread > 0 && (
            <button onClick={markAll} data-testid="notifications-mark-read" className="text-xs text-[#8a6d2f] hover:underline">{t("c.markRead")}</button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">{t("c.noNotifications")}</p>}
          {data.items.map((n) => (
            <button
              key={n.id}
              data-testid={`notification-item-${n.id}`}
              onClick={() => n.link && navigate(n.link)}
              className={`press w-full text-left px-4 py-3 border-b last:border-0 hover:bg-neutral-50 ${n.read ? "" : "bg-[#C5A059]/[0.06]"}`}
            >
              <div className="flex items-center gap-2">
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#C5A059]" />}
                <span className="text-sm font-semibold">{n.title}</span>
              </div>
              <p className="text-xs text-neutral-600 mt-1 leading-relaxed">{n.body}</p>
              <p className="text-[10px] text-neutral-400 mt-1 mono">{fmtDateTime(n.created_at, lang)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
